import { OP_STALE_MS, type OpFairView } from '@/lib/opOdds';
import { perContractFeeCents } from './fees';

export type TheoreticalRoi = { percent: number; reason: null } | { percent: null; reason: string };

/** Conditional passive-fill ROI, per contract, before rebates. The raw bid
 * is the cost; never pass fee-adjusted displayed odds back through this math.
 * Both fair outcomes must be supplied by the admitted exact binary line.
 * Integer/quarter lines need settlement/push mass that the source lacks. */
export function theoreticalEntryRoi(args: {
  fair: OpFairView | null;
  fairSide: 'away' | 'home';
  strike: number;
  priceCents: number | null;
  makerFeeRate: number | null;
  nowMs: number;
  available: boolean;
}): TheoreticalRoi {
  const unavailable = (reason: string): TheoreticalRoi => ({ percent: null, reason });
  const { fair, fairSide, strike, priceCents, makerFeeRate, nowMs, available } = args;
  if (!available) return unavailable('Market unavailable');
  if (!Number.isFinite(strike) || Math.abs(strike % 1) !== 0.5)
    return unavailable('Settlement/push probabilities unavailable');
  if (fair == null || fair.draw != null || fair.anchorBookmaker !== 'pinnacle' || fair.pinnacleWithdrawn != null)
    return unavailable('Exact Pinnacle binary reference unavailable');
  const stamp = fair.anchorUpdatedAtMs;
  if (stamp == null || !Number.isFinite(stamp) || !Number.isFinite(nowMs) || stamp > nowMs || nowMs - stamp > OP_STALE_MS)
    return unavailable('Pinnacle timestamp missing, future, or older than 5 minutes');
  const tenths = fair[fairSide].anchorFairTenths;
  if (tenths == null || !Number.isFinite(tenths) || tenths < 0 || tenths > 1000)
    return unavailable('Same-outcome Pinnacle fair unavailable');
  if (priceCents == null || !Number.isFinite(priceCents) || priceCents <= 0 || priceCents >= 100)
    return unavailable('Passive bid unavailable');
  if (makerFeeRate == null || !Number.isFinite(makerFeeRate) || makerFeeRate < 0)
    return unavailable('Exact maker-fee basis unavailable');
  const fee = perContractFeeCents(priceCents, makerFeeRate);
  const cost = priceCents + fee;
  const percent = 100 * (tenths / 10 - cost) / cost;
  return Number.isFinite(percent) ? { percent, reason: null } : unavailable('Invalid ROI inputs');
}

export function theoreticalRoiText(result: TheoreticalRoi): string {
  if (result.percent == null) return '—';
  const rounded = Math.round(result.percent * 100) / 100;
  return `${rounded > 0 ? '+' : ''}${rounded.toFixed(2)}%`;
}
